import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RemoveCircleOutline as RemoveIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import PromptEvaluationDetail from './PromptEvaluationDetail';
import api from '../api/axios';

interface EvaluationItem {
  press_release_id: string;
  case_name: string;
  reviewed_count: number;
}

interface EvaluationListResponse {
  total_visible_entities: number;
  stats: {
    total: number;
    pending: number;
  };
  results: EvaluationItem[];
}

export function PromptEvaluationListContent() {
  const [data, setData] = useState<EvaluationListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPressReleaseId, setSelectedPressReleaseId] = useState<string | null>(null);

  const loadData = () => {
    setLoading(true);
    api.get<EvaluationListResponse>('/schemes/evaluate/')
      .then((res) => setData(res.data))
      .catch((err) => console.error('Failed to load evaluations:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRowClick = (pressReleaseId: string) => {
    setSelectedPressReleaseId(pressReleaseId);
  };

  const handleBack = () => {
    setSelectedPressReleaseId(null);
    // Refresh the list to pick up any review count changes
    loadData();
  };

  const handleRemoveFromEval = async (pressReleaseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.post(`/schemes/evaluate/${pressReleaseId}/toggle-eval/`);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          results: prev.results.filter((r) => r.press_release_id !== pressReleaseId),
          stats: { ...prev.stats, total: prev.stats.total - 1 },
        };
      });
    } catch (err) {
      console.error('Failed to remove from eval:', err);
    }
  };

  // When a case is selected, show the detail view inline
  if (selectedPressReleaseId) {
    return (
      <PromptEvaluationDetail
        pressReleaseIdProp={selectedPressReleaseId}
        onNavigate={(id) => setSelectedPressReleaseId(id)}
        onBack={handleBack}
        embedded
      />
    );
  }

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalVisible = data?.total_visible_entities || 0;

  return (
    <>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={600}>
            Evals
          </Typography>
          {data && data.stats.pending > 0 && (
            <Chip label={`${data.stats.pending} pending`} size="small" variant="outlined" />
          )}
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        )}
        <Table size="small" sx={{ display: loading ? 'none' : 'table' }}>
          <TableHead>
            <TableRow>
              <TableCell width={40}>#</TableCell>
              <TableCell>Case Name</TableCell>
              <TableCell width={100}>Reviewed</TableCell>
              <TableCell width={40}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.results.map((item, index) => (
              <TableRow
                key={item.press_release_id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleRowClick(item.press_release_id)}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {item.case_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.reviewed_count > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckIcon fontSize="small" color="success" />
                      <Typography variant="body2" color="success.main">
                        {item.reviewed_count}/{totalVisible}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      0/{totalVisible}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Remove from eval set" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => handleRemoveFromEval(item.press_release_id, e)}
                      sx={{ color: 'action.disabled', '&:hover': { color: 'error.main' } }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function PromptEvaluationList() {
  return (
    <Layout>
      <PromptEvaluationListContent />
    </Layout>
  );
}
